import React, { useState, useEffect } from 'react';
import { RRule, RRuleSet, rrulestr, Options, Frequency } from 'rrule';
import moment from "moment-timezone";
import SelectDropdown from '../../../ui/Dropdown/SelectDropdown'; // Assuming this path is correct
import Modal from './Model'; // Assuming this path is correct
import CustomDropdown from '../CustomDropdown';
import Repeat from "../../../../assets/images/repeat.png";

interface RecurrenceSelectorProps {
  value?: string; // The RRULE string
  onChange: (rruleString?: string) => void;
  startTime: string; // ISO string or parsable date string for dtstart
  timeZone: string; // e.g., "America/Chicago"
}

const frequencyOptions = [
  { label: 'Does not repeat', value: 'NONE' },
  { label: 'Daily', value: 'DAILY' },
  { label: 'Weekly', value: 'WEEKLY' },
  { label: 'Monthly', value: 'MONTHLY' },
];

const RecurrenceSelector: React.FC<RecurrenceSelectorProps> = ({ value, onChange, startTime, timeZone }) => {
  const [selectedFreq, setSelectedFreq] = useState<string>('NONE');

  useEffect(() => {
    if (value) {
      try {
        const rrule = rrulestr(value);
        if (rrule.options.freq === RRule.DAILY && rrule.options.interval === 1) {
          setSelectedFreq('DAILY');
        } else if (rrule.options.freq === RRule.WEEKLY && rrule.options.interval === 1 && rrule.options.byweekday?.length === 1) {
          setSelectedFreq('WEEKLY');
        } else if (rrule.options.freq === RRule.MONTHLY && rrule.options.interval === 1) {
          setSelectedFreq('MONTHLY');
        } else {
          setSelectedFreq('NONE'); // Fallback for unrecognized or custom rules
        }
      } catch (e) {
        console.error("Error parsing existing RRULE:", e);
        setSelectedFreq('NONE');
      }
    } else {
      setSelectedFreq('NONE');
    }
  }, [value]);

  const generateRRule = () => {
    if (selectedFreq === 'NONE') {
      onChange(undefined);
      return;
    }

    let rruleOptions: Partial<Options> = {
      dtstart: moment.tz(startTime, timeZone).toDate(), // Ensure dtstart is in the correct timezone
      tzid: timeZone,
    };

    if (selectedFreq === 'DAILY') {
      rruleOptions.freq = RRule.DAILY;
      rruleOptions.interval = 1;
    } else if (selectedFreq === 'WEEKLY') {
      rruleOptions.freq = RRule.WEEKLY;
      rruleOptions.interval = 1;
      rruleOptions.byweekday = [moment.tz(startTime, timeZone).day()]; // Default to start day of week
    } else if (selectedFreq === 'MONTHLY') {
      rruleOptions.freq = RRule.MONTHLY;
      rruleOptions.interval = 1;
      rruleOptions.bymonthday = [moment.tz(startTime, timeZone).date()]; // Default to start day of month
    }

    try {
      const rule = new RRule(rruleOptions as Options);
      onChange(rule.toString());
    } catch (e) {
      console.error("Error generating RRULE:", e);

      onChange(undefined);
    }
  };

  useEffect(() => {
    generateRRule();
  }, [selectedFreq, startTime, timeZone]);

  const handleMainFreqChange = (value: string | number) => {
    const stringValue = String(value);
    setSelectedFreq(stringValue);
  };

  const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <div className="recurrence-selector">
      <CustomDropdown
        label="Repeat"
        name="repeat"
        value={selectedFreq}
        onChange={handleMainFreqChange}
        options={frequencyOptions}
        icon={Repeat}
        overlay={true}
        custome={true}
      />
    </div>
  );
};

export default RecurrenceSelector;
